import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Backdrop from "../../Components/UI/Backdrop/Backdrop";
import ProfileAccount from "../../Components/common/ProfileAccount/ProfileAccount";
import UserCard from "../../Components/common/UserCard/UserCard";
import ProfilePicture from "../../Components/Icon-SVG/ProfilePicture";
import Warning from "../../Components/common/Warning/Warning";
import LogoSettings from "../../Components/common/LogoSettings/LogoSettings";

import { evaluatePassword } from "../../utils/passwordPolicy";

import {
  getAllUsersRequest,
  updateUserRequest,
  publicKeyCheckRequest,
  setUserChanged,
} from "../../store/actions/usersActions";
import { convertNewSystemRequest } from "../../store/actions/analysisReportActions";

import "./styles.scss";

const UserProfile = () => {
  const dispatch = useDispatch();
  const users = useSelector((state) => state?.users.users);
  const userChanged = useSelector((state) => state?.users.userChanged);
  const loadingConvert = useSelector((state) => state?.analysisReports.loadingConvert);
  const defaultEmail = useSelector((state) => state?.users?.user?.email);
  const defaultUserName = useSelector((state) => state?.users?.user?.username);
  const role = useSelector((state) => state?.users?.user?.role) || "USER";
  const authMethod = useSelector((state) => state?.users?.user?.auth_method) || "local";
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");

  const [userNameUpdate, setUserNameUpdate] = useState("");
  const [passwordUpdate, setPasswordUpdate] = useState("");
  const [confirmPasswordUpdate, setConfirmPasswordUpdate] = useState("");
  const [emailUpdate, setEmailUpdate] = useState("");
  const [profileId, setProfileId] = useState(null);

  useEffect(() => {
    dispatch(getAllUsersRequest());
    dispatch(publicKeyCheckRequest());
  }, [dispatch]);

  useEffect(() => {
    if (defaultUserName) setUserNameUpdate(defaultUserName);
    if (defaultEmail) setEmailUpdate(defaultEmail);
  }, [defaultUserName, defaultEmail]);

  useEffect(() => {
    if (userChanged) {
      dispatch(getAllUsersRequest());
      dispatch(setUserChanged(false));
    }
  }, [userChanged, dispatch]);

  // The session user carries no id, so the account is resolved by email once and
  // then remembered — the email itself may change on the next update.
  useEffect(() => {
    if (profileId || !users?.length || !defaultEmail) return;
    const me = users.find((user) => user.email === defaultEmail);
    if (me?._id?.$oid) setProfileId(me._id.$oid);
  }, [users, defaultEmail, profileId]);

  const convertToNewSystem = () => {
    dispatch(convertNewSystemRequest());
  };

  if (!users) {
    return <Backdrop show={!users} />;
  }

  const updateUser = async () => {
    if (!(emailUpdate && userNameUpdate && passwordUpdate && confirmPasswordUpdate)) {
      setError("Please fill in all fields!");
      setShow(true);
      return;
    }

    if (passwordUpdate !== confirmPasswordUpdate) {
      setError("Make sure your passwords are the same");
      setShow(true);
      return;
    }

    const strength = evaluatePassword(passwordUpdate, {
      username: userNameUpdate,
      email: emailUpdate,
    });
    if (!strength.valid) {
      setError(strength.errors[0]);
      setShow(true);
      return;
    }

    if (!profileId) {
      setError("Could not resolve your account, please refresh the page.");
      setShow(true);
      return;
    }

    const pushUser = {
      id: profileId,
      username: userNameUpdate,
      email: emailUpdate,
      password: passwordUpdate,
      isProfile: true,
    };
    dispatch(updateUserRequest(pushUser));
  };

  return (
    <div>
      <div className="userProfile">
        <div className="userProfile__header">
          <h1>User Profile</h1>
          <p>Welcome {defaultUserName}</p>
          {authMethod === "ldap" && (
            <div
              style={{
                marginTop: "10px",
                padding: "8px 12px",
                backgroundColor: "#e8f5e8",
                borderRadius: "4px",
                fontSize: "14px",
                color: "#2e7d32",
                display: "inline-block",
              }}>
              🔐 Authenticated via LDAP
            </div>
          )}
        </div>
        <div className="userProfile__body border">
          <div>
            <UserCard
              name={defaultUserName}
              mail={defaultEmail}
              profileImg={<ProfilePicture width={150} height={150} />}
            />
          </div>
          <hr className="userProfile__body__divider" />
          <div className="userProfile__body__content">
            {authMethod === "ldap" ? (
              <div
                style={{
                  padding: "20px",
                  backgroundColor: "#fff3cd",
                  borderRadius: "4px",
                  border: "1px solid #ffeaa7",
                  textAlign: "center",
                }}>
                <h3 style={{ color: "#856404", marginBottom: "10px" }}>LDAP User</h3>
                <p style={{ color: "#856404", margin: 0 }}>
                  Password changes for LDAP users must be made through your LDAP server.
                </p>
              </div>
            ) : (
              <ProfileAccount
                setUserNameUpdate={setUserNameUpdate}
                userNameUpdate={userNameUpdate}
                setEmailUpdate={setEmailUpdate}
                emailUpdate={emailUpdate}
                passwordUpdate={passwordUpdate}
                setPasswordUpdate={setPasswordUpdate}
                confirmPasswordUpdate={confirmPasswordUpdate}
                setConfirmPasswordUpdate={setConfirmPasswordUpdate}
                button={updateUser}
              />
            )}
          </div>
          {/* <hr className="userProfile__body__divider" />
         {role === "SUPERADMIN" && (
            <div className="userProfile__body__content">
              <div className="profile__card-header">
                <p>Convert to New System</p>
                <p>Please do not use without permission !!</p>
              </div>
              <div className="profile__card-header">
                <button onClick={convertToNewSystem}>Convert</button>
              </div>
            </div>
          )} */}
        </div>
        {role === "SUPERADMIN" && (
          <div className="userProfile__body userProfile__body border">
            <div className="userProfile__body__content m-t-200">
              <LogoSettings button={updateUser} />
            </div>
          </div>
        )}
      </div>
      <Warning
        onClickOk={() => setShow(false)}
        show={show}
        setShow={setShow}
        text={error}
        article="Warning"
        cancelDisable={true}
        textOK="Okay"
      />
      {loadingConvert && (
        <Backdrop
          absolute={true}
          show={loadingConvert}
          message="Please do not refresh the page or close the window!"
        />
      )}
    </div>
  );
};

export default UserProfile;
